import React from "react";
import {
    PlanResource,
    useCheckPlanLimitQuery,
} from "../../redux/services/subscription";
import { usePlanLimit } from "../../hooks/usePlanLimit";

const LABELS: Record<PlanResource, [string, string]> = {
    users: ["user", "users"],
    branches: ["branch", "branches"],
    services: ["service", "services"],
    fleets: ["vehicle", "vehicles"],
};

/**
 * How much of the plan's allowance a resource uses. `isFull` is only true when
 * the API says so — if the check fails we never block the Add button.
 * Refetches automatically after anything that counts toward the limit changes
 * (those mutations invalidate the "planLimit" tag).
 */
export const usePlanUsage = (resource: PlanResource) => {
    // Re-check whenever the page opens or the tab regains focus, on top of the
    // automatic refresh after adds/deletes (the "planLimit" tag).
    const { data } = useCheckPlanLimitQuery(resource, { refetchOnMountOrArgChange: true, refetchOnFocus: true });
    const usage = data && typeof data.current === "number" ? data : undefined;
    const isFull = usage?.allowed === false;
    return { usage, isFull };
};

export const PLAN_FULL_TITLE = "You've reached your plan's limit. Upgrade to add more.";

/** "1 of 2 users used", plus an Upgrade link once the limit is reached. */
export const PlanUsage: React.FC<{ resource: PlanResource }> = ({ resource }) => {
    const { usage, isFull } = usePlanUsage(resource);
    const { openPlans } = usePlanLimit();

    // limit: null means unlimited — nothing to show.
    if (!usage || usage.limit === null || usage.limit === undefined) return null;

    const [singular, plural] = LABELS[resource];
    const noun = usage.limit === 1 ? singular : plural;

    return (
        <div
            className={`flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full border whitespace-nowrap ${
                isFull
                    ? "bg-[#FFF8E6] border-[#F5C451] text-[#5C3D00]"
                    : "bg-[#EEF2FB] border-[#D5DEF3] text-[#143887]"
            }`}
        >
            <span>
                {usage.current} of {usage.limit} {noun} used
            </span>
            {isFull && (
                <button
                    type="button"
                    onClick={() => openPlans()}
                    className="font-semibold text-[#143887] underline hover:text-blue-800"
                >
                    Upgrade
                </button>
            )}
        </div>
    );
};

export default PlanUsage;
